import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronRight, UserPlus, ShieldCheck } from 'lucide-react';

/**
 * Navbar — barra de navegação principal (extraída do Home).
 *
 * @typedef {Object} NavLink
 * @property {string} label Rótulo do link.
 * @property {string} to Rota de destino.
 *
 * @typedef {Object} NavbarProps
 * @property {Array<NavLink>} links Links de navegação.
 * @property {string} [brandName='Com. Águas Vivas'] Nome da marca.
 * @property {string} [brandSubtitle='Caratinga · MG'] Subtítulo da marca.
 * @property {string} [ctaLabel='Seja Membro'] Rótulo do CTA.
 * @property {string} [ctaTo='/celulas'] Rota do CTA.
 */

export default function Navbar({
  links = [],
  brandName = 'Com. Águas Vivas',
  brandSubtitle = 'Caratinga · MG',
  ctaLabel = 'Seja Membro',
  ctaTo = '/celulas',
}) {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/60 bg-white/90 backdrop-blur-xl">
      <div className="app-container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
          <img
            src="/assets/logo-fundo-claro.png"
            alt="Logo Comunidade Águas Vivas"
            className="h-10 w-auto"
            data-testid="home-logo-img"
          />
          <div className="text-left leading-tight">
            <p className="font-heading text-base font-extrabold text-ink">{brandName}</p>
            <p className="text-[11px] font-medium text-ink-faint">{brandSubtitle}</p>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navegação principal">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="rounded-full px-3.5 py-2 font-heading text-sm font-semibold text-ink-soft transition-colors duration-200 hover:bg-slate-100 hover:text-primary"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/admin/login"
            className="hidden items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 font-heading text-sm font-semibold text-ink-soft transition-colors duration-200 hover:border-primary/40 hover:text-primary sm:inline-flex"
            data-testid="navbar-admin-btn"
          >
            <ShieldCheck size={16} /> Área restrita
          </Link>
          <Link
            to={ctaTo}
            className="hidden items-center gap-2 rounded-full bg-primary px-5 py-2.5 font-heading text-sm font-bold text-white shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-dark sm:inline-flex"
          >
            <UserPlus size={16} /> {ctaLabel}
          </Link>
          <button
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-ink-soft transition-colors hover:text-primary lg:hidden"
            aria-label={open ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-slate-100 bg-white lg:hidden">
          <div className="app-container flex flex-col gap-1 py-3">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-xl px-3 py-2.5 font-heading text-sm font-semibold text-ink-soft transition-colors hover:bg-slate-50 hover:text-primary"
              >
                {l.label}
                <ChevronRight size={16} className="text-ink-faint" />
              </Link>
            ))}
            <Link
              to="/admin/login"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between rounded-xl px-3 py-2.5 font-heading text-sm font-semibold text-ink-soft transition-colors hover:bg-slate-50 hover:text-primary"
              data-testid="navbar-admin-btn-mobile"
            >
              Área restrita
              <ShieldCheck size={16} className="text-ink-faint" />
            </Link>
            <Link
              to={ctaTo}
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 font-heading text-sm font-bold text-white"
            >
              <UserPlus size={16} /> {ctaLabel}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
